import API from "@/utils/api"
import { API_COIN_TIER } from "@/app/constants/APIKEYS"
import useSWR from "swr";
import { useEffect, useRef } from "react";

const DEFAULT_TIER = { mmr: 0, ma: 0, max_leverage: 0, notional_min: 0, notional_max: 0 }

const useGetCoinTier = () => {
    const KEYURL = "/exchange/tier";

    const { data = [] , error } = useSWR(
        KEYURL,
        async () => {
            try{
                const response = await API.send('GET', KEYURL, null, {}, false, API_COIN_TIER);
                return response?.data?.tiers || []
            }catch(error){
                console.error("SWR fetch error", error)
                throw error
            }
        },
        { 
            revalidateOnFocus: true, 
            refreshInterval: 1000 * 60 * 10,

            // 에러가 발생하면면 재시도
            shouldRetryOnError: true,
            errorRetryCount: 3
        }
    );

    const hashMapRef = useRef(new Map())

    // data가 변경될 때마다(10분마다) hashMap을 초기화
    useEffect(() => {
        hashMapRef.current = new Map()
    }, [data])

    // hashMap에 코인 티어가 있으면 코인 티어에서 size에 맞는 티어값을 반환해줍니다.
    // 없으면 코인 티어를 조회하고 hashMap에 저장합니다.
    const getCoinTier = (coin: string, amount: number) => {
        if(hashMapRef.current.has(coin)){
            return findTier(coin, amount)
        }else{
            const filteredData = data.filter((item: any) => item.base === coin) || []
            hashMapRef.current.set(coin, filteredData)
            return findTier(coin, amount)
        }
    }

    // 코인 티어에서 size에 맞는 티어값을 반환해줍니다.
    const findTier = (coin: string, amount: number) => {
        const coinTier = hashMapRef.current.get(coin)
        if (!coinTier || coinTier.length === 0) {
            console.error("coinTier가 비어있음:", coin, coinTier);
            return DEFAULT_TIER;
        }

        for(let i = 0; i < coinTier.length; i++){
            if(coinTier[i].notional_min <= amount && amount < coinTier[i].notional_max){
                return coinTier[i]
            }
        }

        console.error("coinTier not found", coinTier)
        return DEFAULT_TIER
    }

    // 현재 레버리지에서 허용되는 최대 명목가를 계산
    const getMaxNotionalForLeverage = (coin: string, leverage: number) => {
        if(hashMapRef.current.has(coin)){
            return findMaxNotionalForLeverage(coin, leverage)
        }else{
            const filteredData = data.filter((item: any) => item.base === coin) || []
            hashMapRef.current.set(coin, filteredData)
            return findMaxNotionalForLeverage(coin, leverage)
        }
    }

    const findMaxNotionalForLeverage = (coin: string, leverage: number) => {
        const coinTier = hashMapRef.current.get(coin)
        
         // 현재 레버리지가 허용되는 가장 마지막 티어를 찾기
        let lastValidTierIndex = -1;
    
        for (let i = 0; i < coinTier.length; i++) {
            const tier = coinTier[i];
            
            // 현재 레버리지가 이 티어에서 허용되는 경우
            if (leverage <= tier.max_leverage) {
                lastValidTierIndex = i;
            }
        }

        // 유효한 티어를 찾았을 때
        if (lastValidTierIndex >= 0) {
            // 다음 티어가 있는 경우 다음 티어의 notionalFloor보다 작은 값 반환
            if (lastValidTierIndex + 1 < coinTier.length) {
                const nextTierStart = coinTier[lastValidTierIndex + 1].notional_min;
                // 다음 티어 시작점보다 0.0001만큼 작은 값을 반환 (소수점 4자리)
                return nextTierStart - 0.0001;
            } else {
                // 마지막 티어인 경우 무제한이지만 실제로는 매우 큰 값
                return Infinity;
            }
        }

        return null
    }


  const getCoinTiersByBase = (base: string) => {

    if (hashMapRef.current.has(base)) {
      return hashMapRef.current.get(base) || [];
    }

    const filteredData = data.filter((item: any) => item.base === base) || [];
    hashMapRef.current.set(base, filteredData);
    return filteredData;
  };

    return { data, getCoinTier, getMaxNotionalForLeverage, getCoinTiersByBase }
}

export default useGetCoinTier
